import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { computeBreaksFromHtml, computeBreaksFromProseMirror } from './pagination';

// Renders A4 page backgrounds behind the ProseMirror content in fullscreen mode.
const BackgroundPagesOverlay = Extension.create({
  name: 'backgroundPagesOverlay',

  addProseMirrorPlugins() {
    const key = new PluginKey('backgroundPagesOverlay');
    return [new Plugin({
      key,
      state: {
        init() { return { changedTransactions: [] }; },
        apply(tr, value) {
          // Отслеживаем изменения документа для пересчета разделителей
          if (tr.docChanged) {
            return { changedTransactions: [...value.changedTransactions, tr] };
          }
          return value;
        }
      },
      view: (view) => {
        let overlay = null;        // слой белой подложки (fullscreen)
        let breaksOverlay = null;  // слой пунктирных авторазделителей (прокручиваемый)
        let isCreatingOverlay = false; // флаг для предотвращения циклического создания
        const A4_WIDTH = 794; // ширина листа A4 в px при 96dpi
        // const PAGE_HEIGHT = 1123; // ОТКЛЮЧЕНО: больше не используется
        let resizeObserver = null;
        let resizeFrame = null;
        const SHOULD_RENDER_BREAK_LINES = false;

        const isFullscreen = () => {
          try {
            return !!(view && view.dom && view.dom.closest('.fullscreen-mode'));
          } catch (error) {
            return false;
          }
        };

        const ensureOverlay = () => {
          if (overlay && overlay.isConnected) return overlay;
          // В полноэкранном режиме создаём фиксированный оверлей высотой окна
          if (!isFullscreen()) return null;

          // ИСПРАВЛЕНИЕ: Ищем стабильный контейнер .tiptap-editor вместо изменяющегося ProseMirror
          let target = view.dom.parentElement;
          while (target && !target.classList.contains('tiptap-editor') && target !== document.body) {
            target = target.parentElement;
          }
          if (!target) {
            console.error('[BackgroundPagesOverlay] Could not find .tiptap-editor container');
            return null;
          }

          // Remove previous overlay if any
          if (overlay && overlay.isConnected) overlay.remove();

          overlay = document.createElement('div');
          overlay.className = 'a4-pages-overlay';
          overlay.style.cssText = [
            'position: absolute', // ИСПРАВЛЕНО: подложка прокручивается вместе с контентом
            'top: 0',
            'left: 0',
            'right: 0',
            'min-height: calc(100% + 60px)', // ИСПРАВЛЕНО: высота с запасом для нижнего отступа
            'z-index: 0',
            'pointer-events: none',
            'width: 100%',
            'padding-bottom: 40px' // ИСПРАВЛЕНО: добавляем нижний отступ для видимости края A4
          ].join(';');

          // a4-container must be positioned
          const cs = getComputedStyle(target);
          if (cs.position === 'static') {
            target.style.position = 'relative';
          }

          target.appendChild(overlay);
          return overlay;
        };

        const ensureBreaksOverlay = () => {
          // НЕ создаем overlay если не в полноэкранном режиме
          if (!isFullscreen()) {
            return null;
          }
          
          // Предотвращаем циклическое создание
          if (isCreatingOverlay) {
            return breaksOverlay;
          }
          
          // Добавляем слой прямо в ProseMirror как первый элемент
          // Уменьшено логирование для предотвращения спама
          
          // Проверяем сначала наш сохраненный reference
          if (breaksOverlay && breaksOverlay.isConnected && breaksOverlay.parentElement) {
            return breaksOverlay;
          }
          
          // Если overlay отключен - очищаем reference
          if (breaksOverlay && !breaksOverlay.isConnected) {
            breaksOverlay = null;
          }
          
          // Привязываем слой к стабильному контейнеру .tiptap-editor,
          // позиционируем его поверх области ProseMirror по вычисленным offset
          let target = view.dom.parentElement;
          while (target && !target.classList.contains('tiptap-editor') && target !== document.body) {
            target = target.parentElement;
          }
          if (!target) return null;
          
          // Target element найден
          
          // Сначала проверяем существующий слой по классу  
          let existingOverlay = null;
          try {
            existingOverlay = target.querySelector('.a4-breaks-overlay');
          } catch (error) {
            console.error('[BackgroundPagesOverlay] ERROR in querySelector:', error);
            return null;
          }
          
          if (existingOverlay) {
            breaksOverlay = existingOverlay;
            return existingOverlay;
          }
          
          // Создаем новый overlay
          
          isCreatingOverlay = true; // Устанавливаем флаг создания
          
          breaksOverlay = document.createElement('div');
          breaksOverlay.className = 'a4-breaks-overlay';
          const pmRect = view.dom.getBoundingClientRect();
          const hostRect = target.getBoundingClientRect();
          const offsetTop = pmRect.top - hostRect.top;
          const offsetLeft = pmRect.left - hostRect.left;

          breaksOverlay.style.cssText = [
            'position: absolute',
            `top: ${Math.round(offsetTop)}px`,
            `left: ${Math.round(offsetLeft)}px`,
            `width: ${Math.round(pmRect.width)}px`,
            'height: 8000px',
            'pointer-events: none !important',
            'z-index: 99999',
            'background: transparent'
          ].join(';');
          
          
          // Добавляем маркер для отладки
          breaksOverlay.setAttribute('data-debug', 'breaks-overlay-' + Date.now());
          
          // Родитель должен быть position: relative
          const targetStyles = getComputedStyle(target);
          if (targetStyles.position === 'static') {
            target.style.position = 'relative';
          }

          // Добавляем слой как первый элемент в .tiptap-editor
          try {
            if (target.firstChild) {
              target.insertBefore(breaksOverlay, target.firstChild);
            } else {
              target.appendChild(breaksOverlay);
            }
          } catch (insertError) {
            console.error('[BackgroundPagesOverlay] Error inserting overlay:', insertError);
            isCreatingOverlay = false;
            return null;
          }
          
          isCreatingOverlay = false; // Сбрасываем флаг создания
          
          // Проверяем, что слой действительно добавлен
          try {
            const check = target.querySelector('.a4-breaks-overlay');
            if (!check) {
              console.error('[BackgroundPagesOverlay] Failed to add overlay to DOM!');
            }
          } catch (checkError) {
            console.error('[BackgroundPagesOverlay] ERROR checking overlay:', checkError);
            return breaksOverlay;
          }
          
          return breaksOverlay;
        };

        const renderPages = () => {
          // console.log('[BackgroundPagesOverlay] renderPages called, isFullscreen:', isFullscreen());
          
          // Если не fullscreen — очищаем подложку и выходим
          if (!isFullscreen()) {
            if (overlay && overlay.isConnected) overlay.remove();
            overlay = null;
            // В fullscreen мы принудительно задаём inline-стили на ProseMirror (padding/width и т.п.),
            // чтобы фоновые страницы совпадали с контентом. При выходе из fullscreen эти стили
            // обязательно нужно сбросить, иначе изменения полей через CSS‑переменные перестают
            // применяться до повторного открытия модалки.
            try {
              const domStyle = view?.dom?.style;
              if (domStyle) {
                domStyle.paddingTop = '';
                domStyle.paddingRight = '';
                domStyle.paddingBottom = '';
                domStyle.paddingLeft = '';
                domStyle.boxSizing = '';
                domStyle.width = '';
                domStyle.maxWidth = '';
                domStyle.margin = '';
              }
            } catch (_) {}
            return;
          }

          // Показываем подложку только в полноэкранном режиме
          const layer = ensureOverlay();
          if (!layer) return;

          // УДАЛЯЕМ устаревший код - теперь используем улучшенный calculateContentBasedBreaks

          // Рисуем один белый лист на всю высоту окна браузера
          layer.innerHTML = '';

          const page = document.createElement('div');
          page.className = 'a4-page';

          // Измеряем реальную высоту содержимого редактора
          const measuredHeight = Math.max(
            view.dom?.scrollHeight || 0,
            view.dom?.offsetHeight || 0,
            0
          );

          // Читаем динамические поля из CSS переменных с fallback к реальным padding'ам.
          // ВАЖНО: значение 0 — валидно, поэтому нельзя использовать `||` (0 воспринимается как false).
          const pmStyles = getComputedStyle(view.dom);
          const readPx = (raw) => {
            const n = parseFloat(String(raw || '').trim());
            return Number.isFinite(n) ? n : null;
          };
          const paddingTop = readPx(pmStyles.getPropertyValue('--a4-padding-top')) ?? readPx(pmStyles.paddingTop) ?? 60;
          const paddingBottom = readPx(pmStyles.getPropertyValue('--a4-padding-bottom')) ?? readPx(pmStyles.paddingBottom) ?? 60;
          const paddingLeft = readPx(pmStyles.getPropertyValue('--a4-padding-left')) ?? readPx(pmStyles.paddingLeft) ?? 60;
          const paddingRight = readPx(pmStyles.getPropertyValue('--a4-padding-right')) ?? readPx(pmStyles.paddingRight) ?? 60;
          

          const MIN_PAGE_HEIGHT = 1123; // Стартовая высота листа A4
          const EXTRA_TAIL = 120; // небольшой запас, чтобы не обрезать нижнюю часть текста
          const totalHeight = Math.max(
            MIN_PAGE_HEIGHT,
            Math.round(measuredHeight + paddingTop + paddingBottom + EXTRA_TAIL)
          );

          page.style.cssText = [
            `width: ${A4_WIDTH}px`,
            `height: ${totalHeight}px`,
            `min-height: ${totalHeight}px`,
            'background: #fff',
            'border: 1px solid #e0e0e0',
            'box-shadow: 0 0 10px rgba(0,0,0,0.1)',
            'margin: 20px auto 40px auto',
            'position: relative',
            'box-sizing: border-box' // ИСПРАВЛЕНО: border включается в ширину
          ].join(';');
          layer.appendChild(page);

          // Гарантируем совпадение внутренних полей с предпросмотром
          const domStyle = view.dom.style;
          domStyle.paddingTop = `${paddingTop}px`;
          domStyle.paddingBottom = `${paddingBottom}px`;
          domStyle.paddingLeft = `${paddingLeft}px`;
          domStyle.paddingRight = `${paddingRight}px`;
          // КРИТИЧЕСКИ ВАЖНО: устанавливаем box-sizing через inline стили
          // так как они имеют приоритет над CSS с !important
          domStyle.boxSizing = 'border-box';
          // ВАЖНО: Ширина должна быть 794px как в стандартном режиме и предпросмотре
          // С box-sizing: border-box padding включается в эту ширину,
          // поэтому реальная ширина контента будет 794 - paddingLeft - paddingRight
          domStyle.width = `${A4_WIDTH}px`;
          domStyle.maxWidth = `${A4_WIDTH}px`;
          // ВАЖНО: при полях=0 не должно быть дополнительных «пустых строк».
          // Ранее здесь было '20px auto', что визуально добавляло отступ сверху/снизу.
          domStyle.margin = '0 auto';
        };


        let lastRenderedSig = '';
        let rafId = null;
        let queuedBreaks = null;
        let latestPreviewBreaks = [];
        let previewActive = false;
        const isDebug = () => {
          try {
            return localStorage.getItem('aot_debug_pagination') === '1';
          } catch (_) { return false; }
        };
        const shouldShowSourceBreaks = () => {
          try {
            if (!isDebug()) return false;
            const ls = localStorage.getItem('aot_debug_source_breaks');
            if (ls === '1') return true;
            return typeof window !== 'undefined' && window.AOT_DEBUG_SOURCE_BREAKS === true;
          } catch (_) {
            return false;
          }
        };
        const getBreaksForRender = () => {
          if (Array.isArray(latestPreviewBreaks) && latestPreviewBreaks.length) {
            return latestPreviewBreaks.slice();
          }
          const dom = view?.dom;
          if (dom) {
            const htmlAttr = dom.getAttribute('data-preview-html');
            if (htmlAttr) {
              const fromHtml = computeBreaksFromHtml(htmlAttr) || [];
              if (fromHtml.length) return fromHtml;
            }
            const fromDom = computeBreaksFromProseMirror(dom) || [];
            if (fromDom.length) return fromDom;
          }
          return [];
        };

        const consumeLatestBreaks = (fallbackToDom = false) => {
          if (previewActive) return;
          const pending = (() => {
            if (Array.isArray(latestPreviewBreaks) && latestPreviewBreaks.length) {
              return latestPreviewBreaks.slice();
            }
            return fallbackToDom ? getBreaksForRender() : [];
          })();
          scheduleRender(pending);
        };

        const handlePreviewBreaks = (event) => {
          const arr = event?.detail?.breaks;
          if (!Array.isArray(arr)) return;
          latestPreviewBreaks = arr.slice();
          if (isDebug()) {
            try { console.table(latestPreviewBreaks.map((y, idx) => ({ idx, previewY: Math.round(y) }))); } catch (_) {}
          }
          lastRenderedSig = '';
          if (isFullscreen() && !previewActive) {
            scheduleRender(latestPreviewBreaks.slice());
          }
        };

        const handlePreviewOpened = () => {
          previewActive = true;
          if (isDebug()) console.log('[BackgroundPagesOverlay] preview opened → pause overlay');
          if (breaksOverlay && breaksOverlay.isConnected) {
            try { breaksOverlay.remove(); } catch (_) {}
          }
          breaksOverlay = null;
          queuedBreaks = null;
          lastRenderedSig = '';
        };

        const handlePreviewClosed = () => {
          previewActive = false;
          if (isDebug()) console.log('[BackgroundPagesOverlay] preview closed → restore overlay');
          if (!isFullscreen()) return;
          renderPages();
          const pending = getBreaksForRender();
          scheduleRender(pending);
        };

        const handlePreviewHtmlChanged = () => {
          if (isFullscreen()) consumeLatestBreaks(true);
        };

        // (удалено) старая сетка — используем общий модуль пагинации

        // (удалено) локальный измерительный алгоритм — используется общий модуль

        const renderBreakLinesNow = (breaks = []) => {
          // Рисуем разделители только в полноэкранном режиме и когда предпросмотр закрыт
          if (!isFullscreen() || previewActive) {
            if (breaksOverlay && breaksOverlay.isConnected) {
              breaksOverlay.remove();
            }
            breaksOverlay = null;
            return;
          }
          
          const layer = ensureBreaksOverlay();
          if (!layer) {
            console.error('[BackgroundPagesOverlay] Could not create or find breaks overlay layer');
            return;
          }
          layer.innerHTML = '';
          const scrollH = (view.dom && view.dom.scrollHeight) || 0;
          layer.style.height = `${scrollH}px`;

          // Новый точный расчёт: берём HTML предпросмотра из data-preview-html
          // (тот же, что показывает PagePreview), с тем же маркером полей
          const pmCS2 = getComputedStyle(view.dom);
          const padTop2 = parseFloat(pmCS2.getPropertyValue('--a4-padding-top')) || parseFloat(pmCS2.paddingTop) || 0;
          const padBottom2 = parseFloat(pmCS2.getPropertyValue('--a4-padding-bottom')) || parseFloat(pmCS2.paddingBottom) || 0;
          const padLeft2 = parseFloat(pmCS2.getPropertyValue('--a4-padding-left')) || parseFloat(pmCS2.paddingLeft) || 0;
          const padRight2 = parseFloat(pmCS2.getPropertyValue('--a4-padding-right')) || parseFloat(pmCS2.paddingRight) || 0;
          let html = view.dom.getAttribute('data-preview-html');
          if (!html || !html.includes('data-a4-margins')) {
            const marker = `<div data-a4-margins='{"top":${Math.round(padTop2)},"right":${Math.round(padRight2)},"bottom":${Math.round(padBottom2)},"left":${Math.round(padLeft2)}}'></div>`;
            html = marker + view.dom.innerHTML; // fallback
          }
          const debugMode = isDebug();
          const hasExternalBreaks = Array.isArray(breaks) && breaks.length;
          let sourceBreaks = hasExternalBreaks ? breaks.slice() : (computeBreaksFromHtml(html) || []);
          if (!Array.isArray(sourceBreaks)) sourceBreaks = [];
          if (!hasExternalBreaks && !sourceBreaks.length) {
            sourceBreaks = computeBreaksFromProseMirror(view.dom) || [];
            if (debugMode) console.log('[BackgroundPagesOverlay] fallback sourceBreaks', sourceBreaks);
          }
          if (!sourceBreaks.length) {
            sourceBreaks = getBreaksForRender();
          }
          if (!Array.isArray(sourceBreaks)) {
            sourceBreaks = [];
          }
          if (!hasExternalBreaks && sourceBreaks.length) {
            latestPreviewBreaks = sourceBreaks.slice();
          }
          breaks = sourceBreaks.slice();

          let snapEnabled = false;
          try {
            const ls = localStorage.getItem('AOT_SNAP_BETWEEN_LINES');
            if (ls === '1') snapEnabled = true;
            if (typeof window !== 'undefined') {
              if (window.AOT_SNAP_BETWEEN_LINES === true) snapEnabled = true;
              if (window.AOT_SNAP_BETWEEN_LINES === false) snapEnabled = false;
            }
          } catch(_) {}
          const containerRect = view.dom.getBoundingClientRect();
          const contentLeft2 = containerRect.left + padLeft2;
          const contentRight2 = containerRect.right - padRight2;
          // Детеминированный снап: приоритет TR для таблиц; иначе line rects
          const snapToCaretLine = (y) => {
            const yView = containerRect.top + y;
            const RADIUS = 12; // ±12px радиус поиска
            // Находим ближайший блочный контейнер вокруг точки
            const probeX = Math.round((contentLeft2 + contentRight2) / 2);
            let el = document.elementFromPoint(probeX, Math.round(yView));
            while (el && el !== view.dom && !(/^(P|DIV|LI|TD|TH|H1|H2|H3|H4|H5|H6|TABLE)$/).test(el.tagName)) {
              el = el.parentElement;
            }
            const block = (el && el !== view.dom) ? el : view.dom;

            // Собираем прямоугольники строк текста внутри блока и границы TR для таблиц
            const lineRects = [];
            const rowRects = [];
            try {
              // Текстовые line rects
              const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
                acceptNode(node) {
                  return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
                }
              });
              let node;
              while ((node = walker.nextNode())) {
                try {
                  const r = document.createRange();
                  r.setStart(node, 0);
                  r.setEnd(node, node.length);
                  const rs = r.getClientRects();
                  for (let i = 0; i < rs.length; i++) lineRects.push(rs[i]);
                } catch(_) {}
              }
              // Границы строк таблицы (TR) — приоритетные
              const table = block.closest && block.closest('table');
              const rootForRows = table || block;
              const trs = rootForRows.querySelectorAll ? rootForRows.querySelectorAll('tr') : [];
              trs.forEach(tr => {
                try { const rr = tr.getBoundingClientRect(); if (rr && rr.height > 0) rowRects.push(rr); } catch(_) {}
              });
            } catch(_) {}

            // Функция ограничивает координату в пределах текущей страницы
            const clampToPage = (yy) => {
              const PAGE_HEIGHT = 1123;
              const pageContent = PAGE_HEIGHT - padTop2 - padBottom2;
              const pageIndex = Math.max(0, Math.floor((y - padTop2) / pageContent));
              const base = padTop2 + pageIndex * pageContent;
              const minY = base + 1;
              const maxY = base + pageContent - 1;
              let snapped = Math.round(yy - containerRect.top);
              if (snapped < minY) snapped = minY;
              if (snapped > maxY) snapped = maxY;
              return Math.max(0, snapped);
            };

            // Обработчик выбора позиции по множеству rects по заданному правилу
            const chooseByRule = (rects, label) => {
              if (!rects || !rects.length) return null;
              // 1) Если попадаем внутрь line-rect — берём top этой строки − 1px
              for (const r of rects) {
                if (r.top <= yView && yView <= r.bottom) {
                  const snapY = clampToPage(r.top - 1);
                  try { if (isDebug()) console.log(`[AOT][Overlay][snap] ${label||'rect'} inside -> top-1`, { y, yView, rect: { top: r.top, bottom: r.bottom }, snapY }); } catch(_) {}
                  return snapY;
                }
              }
              // 2) Иначе берём bottom ближайшей строки строго выше previewY (−1px) в радиусе
              let bestBelow = null; // ближайшая сверху
              for (const r of rects) {
                if (r.bottom <= yView - 1 && (yView - r.bottom) <= RADIUS) {
                  const d = yView - r.bottom;
                  if (!bestBelow || d < bestBelow.d) bestBelow = { r, d };
                }
              }
              if (bestBelow) {
                const snapY = clampToPage(bestBelow.r.bottom - 1);
                try { if (isDebug()) console.log(`[AOT][Overlay][snap] ${label||'rect'} above -> bottom-1`, { y, yView, d: bestBelow.d, rect: { top: bestBelow.r.top, bottom: bestBelow.r.bottom }, snapY }); } catch(_) {}
                return snapY;
              }
              return null;
            };

            // Приоритет для TR границ
            const trChoice = chooseByRule(rowRects, 'TR');
            if (trChoice != null) return trChoice;

            // Затем обычные текстовые line rects
            const lnChoice = chooseByRule(lineRects, 'LINE');
            if (lnChoice != null) return lnChoice;

            // Если в радиусе ничего — оставляем исходную позицию предпросмотра
            return Math.max(0, Math.round(y));
          };
          let finalBreaks = [];
          if (Array.isArray(breaks) && breaks.length) {
            if (snapEnabled) {
              const prevDisplay = layer.style.display;
              layer.style.display = 'none';
              try {
                finalBreaks = breaks.map(snapToCaretLine).sort((a,b)=>a-b);
              } finally {
                layer.style.display = prevDisplay;
              }
            } else {
              finalBreaks = breaks.slice().sort((a,b)=>a-b);
            }
          }

          const manualNodes = Array.from(view.dom.querySelectorAll('div[data-type="page-spacer"], div[data-type="physical-page-break"]'));
          if (manualNodes.length && finalBreaks.length) {
            const manualPositions = manualNodes
              .map((node) => {
                const rect = node.getBoundingClientRect();
                if (!rect || !Number.isFinite(rect.top)) return null;
                return Math.max(0, Math.round(rect.top - containerRect.top));
              })
              .filter((pos) => typeof pos === 'number' && !Number.isNaN(pos));

            if (manualPositions.length) {
              const SNAP_THRESHOLD = 4;
              const filtered = finalBreaks.filter(y => manualPositions.every(man => Math.abs(man - Math.round(y)) > SNAP_THRESHOLD));
              finalBreaks = filtered.concat(manualPositions).sort((a,b)=>a-b);
            }
          }
          // console.log('[BackgroundPagesOverlay] Rendering', breaks.length, 'break lines');
          // console.log('[BackgroundPagesOverlay] Layer element:', layer);
          // console.log('[BackgroundPagesOverlay] Layer parent:', layer.parentElement);
          
          if (debugMode) {
            const diff = finalBreaks.map((y, i) => ({ i, preview: sourceBreaks[i], snapped: y, delta: (y - (sourceBreaks[i] ?? y)) }));
            console.table(diff);
            console.log('[AOT][Overlay] margins=', { padTop: Math.round(padTop2), padBottom: Math.round(padBottom2), padLeft: Math.round(padLeft2), padRight: Math.round(padRight2) }, 'breaks=', finalBreaks);
          }

          if (SHOULD_RENDER_BREAK_LINES && finalBreaks.length) {
            finalBreaks.forEach((y, index) => {
              const line = document.createElement('div');
              line.className = `page-break-line page-break-${index}`;
              // Слой находится внутри ProseMirror, координаты 1:1
              const adjustedY = Math.max(0, Math.round(y));
              
              line.style.cssText = [
                'position: absolute',
                `top: ${adjustedY}px`,
                'left: 0',
                `width: 100%`, // Используем 100% ширины слоя
                'height: 2px',
                'border-top: 2px dashed #007bff',
                'background: transparent',
                'z-index: 1000',
                'pointer-events: none !important'
              ].join(';');
              
              layer.appendChild(line);

              // В отладочном режиме рисуем тонкую полупрозрачную ЖЁЛТУЮ линию на исходной позиции предпросмотра
              if (debugMode && shouldShowSourceBreaks() && sourceBreaks[index] != null) {
                const dbg = document.createElement('div');
                dbg.className = `page-break-line-original page-break-orig-${index}`;
                dbg.style.cssText = [
                  'position:absolute',
                  `top:${Math.max(0, Math.round(sourceBreaks[index]))}px`,
                  'left:0',
                  'width:100%',
                  'height:1px',
                  'background: rgba(255, 215, 0, 0.8)',
                  'z-index: 1001',
                  'pointer-events:none'
                ].join(';');
                layer.appendChild(dbg);
              }
            });
          }

          if (SHOULD_RENDER_BREAK_LINES) {
            try {
              layer.querySelectorAll('.page-break-line');
            } catch (linesError) {
              console.error('[BackgroundPagesOverlay] ERROR checking lines:', linesError);
            }
          }
          
          
          // Защищаем слой от удаления
          if (layer && layer.parentElement) {
            layer.setAttribute('data-protected', 'true');
            // тихий режим
          }
          
          // Сохраняем комбинированную сигнатуру с полями
          const cs = getComputedStyle(view.dom);
          const padTop = parseFloat(cs.getPropertyValue('--a4-padding-top')) || 60;
          const padBottom = parseFloat(cs.getPropertyValue('--a4-padding-bottom')) || 60;
          const marginsSig = `${padTop}_${padBottom}`;
          const breaksSig = Array.isArray(finalBreaks) ? finalBreaks.map(n => Math.round(n)).join(',') : '';
          lastRenderedSig = `${marginsSig}:${breaksSig}`;
        };

        const scheduleRender = (br = []) => {
          queuedBreaks = br;
          if (previewActive) {
            return;
          }
          if (rafId) return;
          rafId = requestAnimationFrame(() => {
            rafId = null;
            
            // Включаем поля A4 в сигнатуру для обнаружения их изменений
            const cs = getComputedStyle(view.dom);
            const padTop = parseFloat(cs.getPropertyValue('--a4-padding-top')) || 60;
            const padBottom = parseFloat(cs.getPropertyValue('--a4-padding-bottom')) || 60;
            const marginsSig = `${padTop}_${padBottom}`;
            
            const breaksSig = Array.isArray(queuedBreaks) ? queuedBreaks.map(n => Math.round(n)).join(',') : '';
            const sig = `${marginsSig}:${breaksSig}`; // Комбинированная сигнатура с полями
            
            const isEmptyArrayRecalc = Array.isArray(queuedBreaks) && queuedBreaks.length === 0;
            
            // ИСПРАВЛЕНО: Если передан пустой массив - это сигнал для принудительного пересчета
            if (isEmptyArrayRecalc) {
              if (isDebug()) console.log('[BackgroundPagesOverlay] 🔄 Принудительный пересчет (пустой массив)');
              const fallback = getBreaksForRender();
              renderBreakLinesNow(fallback);
              return;
            }
            
            // НЕ пересоздаем overlay если он все еще подключен и данные те же
            if (breaksOverlay && breaksOverlay.isConnected && sig === lastRenderedSig) {
              if (isDebug()) console.log('[BackgroundPagesOverlay] Overlay exists and signature unchanged, skipping render');
              return;
            }
            
            if (sig !== lastRenderedSig) {
              renderBreakLinesNow(queuedBreaks);
            }
          });
        };

        let isHandlingFullscreen = false;
        const handleFullscreenEvent = () => {
          if (isHandlingFullscreen) return;
          isHandlingFullscreen = true;
          
          renderPages();
          if (isFullscreen()) {
            setTimeout(() => {
              if (isDebug()) console.log('[AOT][Overlay] fullscreen enter → initial render');
              consumeLatestBreaks(true);
              isHandlingFullscreen = false;
            }, 120);
          } else {
            isHandlingFullscreen = false;
          }
        };
        const handleResize = () => { 
          if (isDebug()) console.log('[BackgroundPagesOverlay] Resize');
          renderPages(); 
          const pending = getBreaksForRender();
          scheduleRender(pending); 
        };

        // Debounced функция для предотвращения слишком частых пересчетов
        let recalculateTimeout = null;
        let lastEventTime = 0;
        const handleAutoBreaks = (event) => {
          const now = Date.now();
          
          // Предотвращаем спам событий (не чаще раза в 50ms)
          if (now - lastEventTime < 50) {
            return;
          }
          lastEventTime = now;
          
          const detailBreaks = event?.detail?.breaks;
          if (Array.isArray(detailBreaks) && detailBreaks.length) {
            latestPreviewBreaks = detailBreaks.slice();
            if (isFullscreen() && !previewActive) {
              scheduleRender(latestPreviewBreaks.slice());
            }
            return;
          }

          if (!isFullscreen()) {
            return;
          }
          
          
          // Отменяем предыдущий таймер
          if (recalculateTimeout) {
            clearTimeout(recalculateTimeout);
          }
          
          // Используем наш упрощенный алгоритм вместо данных от AutoPaginationExtension
          recalculateTimeout = setTimeout(() => {
            consumeLatestBreaks(true);
            recalculateTimeout = null;
          }, 100);
        };

        // Специальный обработчик для изменения полей A4
        const handleMarginsChange = (event) => {
          if (isDebug()) console.log('[BackgroundPagesOverlay] 🔧 Поля A4 изменились, пересчёт', event?.detail);

          if (!isFullscreen()) {
            if (isDebug()) console.log('[BackgroundPagesOverlay] ⚠️ Не в полноэкранном режиме, игнорируем изменение полей');
            return;
          }
          
          // Принудительно очищаем кэш и пересчитываем
          lastRenderedSig = ''; // Сбрасываем кэш для принудительного перерисовка
          
          // Немедленно пересчитываем разделители при изменении полей
          setTimeout(() => {
            if (!isFullscreen()) return;
            if (isDebug()) console.log('[BackgroundPagesOverlay] 🚀 Принудительный пересчет после изменения полей');
            renderPages();
            const pending = getBreaksForRender();
            scheduleRender(pending);
          }, 150); // Увеличиваю задержку для гарантированного применения CSS изменений
        };

        // Удалены устаревшие функции контент-анализа и измерения — используем сетку A4
        
        // ИСПРАВЛЕНО: Initial render с простой системой разделителей
        // Вызываем enhanceEmptyParagraphsInDOM только один раз при инициализации
        const initializeEditor = () => {
          if (isFullscreen() && view && view.dom) {
            // Обрабатываем пустые параграфы только при первой инициализации
            const paragraphs = view.dom.querySelectorAll('p');
            
            paragraphs.forEach((p, index) => {
              const textContent = p.textContent || '';
              const hasOnlyBr = p.childNodes.length === 1 && 
                               p.firstChild && 
                               p.firstChild.tagName === 'BR';
              const isEmpty = textContent.trim() === '';
              
              if (isEmpty || hasOnlyBr) {
                // ИСПРАВЛЕНО: Наследуем размеры от предыдущего параграфа, если он есть
                let fontSize, lineHeight;
                
                // Ищем предыдущий параграф с содержимым
                let prevP = null;
                for (let i = index - 1; i >= 0; i--) {
                  const candidate = paragraphs[i];
                  if (candidate && candidate.textContent && candidate.textContent.trim()) {
                    prevP = candidate;
                    break;
                  }
                }
                
                const refEl = prevP || p;
                const cs2 = window.getComputedStyle(refEl);
                fontSize = parseFloat(cs2.fontSize) || 14;
                const lhRaw = cs2.lineHeight;
                if (lhRaw === 'normal' || lhRaw === 'initial' || lhRaw === 'inherit') {
                  lineHeight = Math.round(fontSize * 1.15); // синхронизация с редактором (line-height: 1.15)
                } else if (lhRaw && lhRaw.endsWith('px')) {
                  lineHeight = parseFloat(lhRaw);
                } else if (lhRaw && lhRaw.endsWith('em')) {
                  lineHeight = parseFloat(lhRaw) * fontSize;
                } else if (!isNaN(parseFloat(lhRaw))) {
                  lineHeight = parseFloat(lhRaw) * fontSize;
                } else {
                  lineHeight = Math.round(fontSize * 1.15);
                }
                
                // Принудительно устанавливаем стили для пустых параграфов
                p.style.minHeight = `${lineHeight}px`;
                p.style.lineHeight = `${lineHeight}px`;
                p.style.fontSize = `${fontSize}px`;
                p.style.display = 'block';
                
                // Добавляем data-атрибут для CSS селекторов
                p.setAttribute('data-empty', 'true');
                
                if (isEmpty && !hasOnlyBr) {
                  // Добавляем невидимый контент для правильного измерения
                  if (!p.querySelector('.empty-space-marker')) {
                    const span = document.createElement('span');
                    span.className = 'empty-space-marker';
                    span.style.opacity = '0';
                    span.textContent = '\u00A0';
                    p.appendChild(span);
                  }
                }
              }
            });
            
            // Обработаны пустые параграфы
          }
          
          renderPages(); 
          consumeLatestBreaks(true); // fallback к расчёту через предпросмотр/DOM
        };
        
        requestAnimationFrame(() => setTimeout(initializeEditor, 120));
        
        // Экспортируем отладочные функции в глобальную область
        try {
          window.__A4_DEBUG = Object.assign({}, window.__A4_DEBUG, {
            enabled: true, // Включаем отладку по умолчанию для диагностики
            renderBreaks: () => consumeLatestBreaks(true),
            forceBreaks: () => {
              console.log('Force rendering breaks, isFullscreen:', isFullscreen());
              consumeLatestBreaks(true);
            },
            checkDom: () => {
              console.log('=== DOM Check ===');
              const proseMirror = view.dom;
              
              if (!proseMirror) {
                console.error('ProseMirror DOM element not found');
                return;
              }
              
              console.log('ProseMirror element:', proseMirror);
              console.log('ProseMirror position:', getComputedStyle(proseMirror).position);
              
              // Ищем overlay в ProseMirror
              let overlay = null;
              try {
                console.log('[BackgroundPagesOverlay] DEBUG: Calling proseMirror.querySelector');
                overlay = proseMirror.querySelector('.a4-breaks-overlay');
                console.log('[BackgroundPagesOverlay] DEBUG: querySelector result:', !!overlay);
              } catch (debugError) {
                console.error('[BackgroundPagesOverlay] DEBUG ERROR in querySelector:', debugError);
                console.error('[BackgroundPagesOverlay] DEBUG proseMirror:', proseMirror);
                console.error('[BackgroundPagesOverlay] DEBUG proseMirror type:', typeof proseMirror);
                return;
              }
              
              if (overlay) {
                console.log('Overlay found:', overlay);
                console.log('Overlay position:', getComputedStyle(overlay).position);
                console.log('Overlay z-index:', getComputedStyle(overlay).zIndex);
                console.log('Overlay dimensions:', overlay.getBoundingClientRect());
                console.log('ProseMirror dimensions:', proseMirror.getBoundingClientRect());
                
                let lines = [];
                try {
                  console.log('[BackgroundPagesOverlay] DEBUG: Calling overlay.querySelectorAll');
                  lines = overlay.querySelectorAll('.page-break-line');
                  console.log('[BackgroundPagesOverlay] DEBUG: querySelectorAll result count:', lines.length);
                } catch (debugQueryError) {
                  console.error('[BackgroundPagesOverlay] DEBUG ERROR in overlay.querySelectorAll:', debugQueryError);
                  console.error('[BackgroundPagesOverlay] DEBUG overlay:', overlay);
                  console.error('[BackgroundPagesOverlay] DEBUG overlay type:', typeof overlay);
                  return;
                }
                console.log('Break lines found:', lines.length);
                lines.forEach((line, i) => {
                  const rect = line.getBoundingClientRect();
                  const styles = getComputedStyle(line);
                  console.log(`Line ${i}: top=${styles.top}, border=${styles.borderTop}, rect=`, rect);
                });
              } else {
                console.log('No overlay found in ProseMirror!');
              }
              
              // Проверяем есть ли другие элементы которые могут перекрывать
              let allDivs = [];
              try {
                console.log('[BackgroundPagesOverlay] DEBUG: Calling proseMirror.querySelectorAll("div")');
                allDivs = proseMirror.querySelectorAll('div');
                console.log('[BackgroundPagesOverlay] DEBUG: div querySelectorAll result count:', allDivs.length);
              } catch (debugDivError) {
                console.error('[BackgroundPagesOverlay] DEBUG ERROR in div querySelectorAll:', debugDivError);
                console.error('[BackgroundPagesOverlay] DEBUG proseMirror for div query:', proseMirror);
                console.error('[BackgroundPagesOverlay] DEBUG proseMirror type for div query:', typeof proseMirror);
                return;
              }
              console.log('Total divs in ProseMirror:', allDivs.length);
            }
          });
        } catch(_) {}
        // Listen to fullscreen toggle events emitted by editor
        if (view && view.dom) {
          try {
            view.dom.addEventListener('fullscreenModeChanged', handleFullscreenEvent);
            // ИСПРАВЛЕНИЕ: Добавляем слушатель изменения полей A4
            view.dom.addEventListener('pageMarginsChanged', handleMarginsChange);
            if (isDebug()) console.log('[BackgroundPagesOverlay] listeners attached');
          } catch (error) {
            console.error('[BackgroundPagesOverlay] Error adding event listeners:', error);
          }
        }
        // Создаем усиленный MutationObserver для отладки удаления overlay
        let mutationProcessing = false;
        let lastMutationTime = 0;
        let mo = null;
        
        // Создаем MutationObserver только если view.dom существует
        if (view && view.dom) {
          try {
            mo = new MutationObserver((mutations) => {
              const now = Date.now();
              
              // Предотвращаем рекурсию и чрезмерную частоту вызовов
              if (mutationProcessing || now - lastMutationTime < 100) return;
              
              lastMutationTime = now;
              mutationProcessing = true;
              
              let overlayRemoved = false;
              let removalInfo = null;
              
              // Проверяем, не являются ли изменения нашими overlay элементами
              const isOurChange = mutations.some(mutation => {
                try {
                  // Проверяем наши overlay элементы
                  if (mutation.target && mutation.target.classList) {
                    if (mutation.target.classList.contains('a4-breaks-overlay') || 
                        mutation.target.classList.contains('a4-pages-overlay')) {
                      return true;
                    }
                  }
                  
                  // Проверяем добавленные узлы
                  for (let node of mutation.addedNodes) {
                    if (node.nodeType === 1 && node.classList) { // Element node
                      if (node.classList.contains('a4-breaks-overlay') || 
                          node.classList.contains('a4-pages-overlay') ||
                          node.classList.contains('page-break-line')) {
                        return true;
                      }
                    }
                  }
                  
                  // ДЕТАЛЬНАЯ ПРОВЕРКА удаленных узлов
                  for (let node of mutation.removedNodes) {
                    if (node.nodeType === 1 && node.classList) {
                      if (node.classList.contains('a4-breaks-overlay')) {
                        overlayRemoved = true;
                        removalInfo = {
                          mutation: mutation,
                          removedNode: node,
                          target: mutation.target,
                          type: mutation.type,
                          debug: node.getAttribute('data-debug')
                        };
                        console.error('[BackgroundPagesOverlay] 🚨 OVERLAY REMOVED BY MUTATION!');
                        console.error('[BackgroundPagesOverlay] Mutation details:', removalInfo);
                        console.error('[BackgroundPagesOverlay] Stack trace:', new Error().stack);
                      }
                    }
                  }
                  
                  return false;
                } catch (error) {
                  console.warn('[BackgroundPagesOverlay] Error processing mutation:', error);
                  return false;
                }
              });
              
              // Если overlay был удален - не пересоздаем его сразу, чтобы избежать цикла
              if (overlayRemoved) {
                console.error('[BackgroundPagesOverlay] Overlay removal detected - clearing reference');
                breaksOverlay = null;
                return; // НЕ вызываем renderPages сразу
              }
              
              // Если это не наши изменения, проверяем нужно ли восстановить overlay
              if (!isOurChange) {
                // Проверяем существует ли overlay в полноэкранном режиме
                if (isFullscreen() && (!breaksOverlay || !breaksOverlay.isConnected)) {
                  renderPages();
                  
                  // ИСПРАВЛЕНО: Пересчитываем разделители на основе нового содержимого
                 setTimeout(() => {
                    if (isFullscreen()) {
                      consumeLatestBreaks(true);
                    }
                  }, 150);
                } else {
                  renderPages();
                }
              }
              
              // Всегда сбрасываем флаг в конце
              setTimeout(() => { 
                mutationProcessing = false; 
              }, 100);
            });
            mo.observe(view.dom, { childList: true, subtree: true, characterData: true });
            if (isDebug()) console.log('[BackgroundPagesOverlay] Enhanced MutationObserver created for debugging');
          } catch (error) {
            console.error('[BackgroundPagesOverlay] Error creating MutationObserver:', error);
          }
        }
        if (typeof ResizeObserver !== 'undefined' && view?.dom) {
          try {
            resizeObserver = new ResizeObserver(() => {
              if (resizeFrame !== null) return;
              resizeFrame = window.requestAnimationFrame(() => {
                resizeFrame = null;
                // Подстраиваем белую подложку под фактическую высоту текста
                renderPages();
              });
            });
            resizeObserver.observe(view.dom);
          } catch (error) {
            if (isDebug()) console.warn('[BackgroundPagesOverlay] ResizeObserver init failed:', error);
          }
        }

        window.addEventListener('resize', handleResize);

        // Гарантированный первый рендер в полноэкранном режиме
        setTimeout(() => {
          if (isFullscreen()) {
            if (isDebug()) console.log('[AOT][Overlay] init render');
            renderPages();
            const pending = getBreaksForRender();
            scheduleRender(pending);
          }
        }, 150);

        // Перерисовываем линии при смене data-preview-html
        if (view && view.dom) {
          try {
            view.dom.addEventListener('previewOpened', handlePreviewOpened);
            view.dom.addEventListener('previewClosed', handlePreviewClosed);
            view.dom.addEventListener('previewHtmlChanged', handlePreviewHtmlChanged);
            view.dom.addEventListener('autoPageBreaks', handleAutoBreaks);
            view.dom.addEventListener('prosemirrorContentChanged', handleAutoBreaks);
            view.dom.addEventListener('pageSpacer', handleAutoBreaks);
          } catch(_) {}
        }

        window.addEventListener('previewBreaksUpdated', handlePreviewBreaks);

        return {
          update: (view, prevState) => {
            // Проверяем, были ли изменения в документе
            const prevTransactions = key.getState(prevState)?.changedTransactions || [];
            const currentTransactions = key.getState(view.state)?.changedTransactions || [];
            
            if (currentTransactions.length > prevTransactions.length) {
              if (isDebug()) console.log(`[BackgroundPagesOverlay] 🔄 Обнаружены изменения документа, перерасчитываем разделители`);
              // Планируем пересчет разделителей
              setTimeout(() => {
                if (isFullscreen()) {
                  if (isDebug()) console.log('[BackgroundPagesOverlay] ⚡ ЗАПУСКАЕМ ПЕРЕРИСОВКУ после изменения документа');
                  consumeLatestBreaks(true);
                }
              }, 50);
            }
            
            renderPages();
          },
          destroy: () => {
            // Очищаем таймеры
            if (recalculateTimeout) {
              clearTimeout(recalculateTimeout);
              recalculateTimeout = null;
            }
            if (rafId) {
              cancelAnimationFrame(rafId);
              rafId = null;
            }
            
            // Очищаем DOM элементы
            if (overlay && overlay.isConnected) overlay.remove();
            overlay = null;
            if (breaksOverlay && breaksOverlay.isConnected) breaksOverlay.remove();
            breaksOverlay = null;
            
            // Удаляем слушатели событий
            if (view && view.dom) {
              view.dom.removeEventListener('fullscreenModeChanged', handleFullscreenEvent);
              view.dom.removeEventListener('previewOpened', handlePreviewOpened);
              view.dom.removeEventListener('previewClosed', handlePreviewClosed);
              view.dom.removeEventListener('previewHtmlChanged', handlePreviewHtmlChanged);
              view.dom.removeEventListener('autoPageBreaks', handleAutoBreaks);
              view.dom.removeEventListener('prosemirrorContentChanged', handleAutoBreaks);
              view.dom.removeEventListener('pageSpacer', handleAutoBreaks);
              view.dom.removeEventListener('pageMarginsChanged', handleMarginsChange);

              // Сбрасываем принудительно установленные inline-стили
              const domStyle = view.dom.style;
              domStyle.paddingTop = '';
              domStyle.paddingRight = '';
              domStyle.paddingBottom = '';
              domStyle.paddingLeft = '';
              domStyle.boxSizing = '';
              domStyle.width = '';
              domStyle.maxWidth = '';
              domStyle.margin = '';
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('previewBreaksUpdated', handlePreviewBreaks);
            
            // Отключаем MutationObserver
            if (mo) {
              mo.disconnect();
            }
            if (resizeObserver) {
              try {
                resizeObserver.disconnect();
              } catch(_) {}
              resizeObserver = null;
            }
            if (resizeFrame !== null) {
              try { cancelAnimationFrame(resizeFrame); } catch(_) {}
              resizeFrame = null;
            }
          },
        };
      },
    })];
  },
});

export default BackgroundPagesOverlay;
